import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const Blogs = () => {
  const navigate = useNavigate();
  const [visibleArticles, setVisibleArticles] = useState(6);
  const [loading, setLoading] = useState(false);

  const allArticles = [
    {
      slug: 'interior-design-trends-2025',
      title: "Top 10 Interior Design Trends for 2025",
      excerpt: "Discover the latest interior design trends that are reshaping modern homes. From sustainable materials to smart home integration.",
      category: "Interior Design",
      date: "Jan 15, 2025",
      author: "Priya Sharma",
      image: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=85&w=800",
      readTime: "5 min read"
    },
    {
      slug: 'property-buyers-guide',
      title: "How to Choose the Right Property: A Buyer's Guide",
      excerpt: "Essential tips for first-time home buyers in India. Learn about location analysis, documentation, and making the right investment.",
      category: "Real Estate",
      date: "Jan 12, 2025",
      author: "Rajesh Kumar",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=85&w=800",
      readTime: "7 min read"
    },
    {
      slug: 'property-management-roi',
      title: "Maximizing ROI: Property Management Best Practices",
      excerpt: "Learn how professional property management can increase your rental income and property value while reducing hassles.",
      category: "Property Management",
      date: "Jan 10, 2025",
      author: "Anita Desai",
      image: "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?q=85&w=800",
      readTime: "6 min read"
    },
    {
      slug: 'sustainable-interior-design',
      title: "Sustainable Interior Design: Eco-Friendly Materials Guide",
      excerpt: "Explore sustainable materials and practices for creating beautiful, environmentally responsible interiors.",
      category: "Interior Design",
      date: "Jan 8, 2025",
      author: "Vikram Patel",
      image: "https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?q=85&w=800",
      readTime: "8 min read"
    },
    {
      slug: 'tier2-cities-investment',
      title: "Real Estate Investment Strategies in Tier 2 Cities",
      excerpt: "Why tier 2 cities are becoming hotspots for real estate investment and how to capitalize on this trend.",
      category: "Real Estate",
      date: "Jan 5, 2025",
      author: "Meera Iyer",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=800",
      readTime: "6 min read"
    },
    {
      slug: 'smart-home-integration',
      title: "Smart Home Integration: The Future of Living",
      excerpt: "How IoT and smart home technology are transforming modern living spaces and property values.",
      category: "Technology",
      date: "Jan 3, 2025",
      author: "Arjun Mehta",
      image: "https://images.unsplash.com/photo-1558002038-1055907df827?q=85&w=800",
      readTime: "5 min read"
    },
    {
      slug: 'kitchen-design-trends',
      title: "Kitchen Design Trends: Modern vs Traditional",
      excerpt: "Compare modern minimalist kitchens with traditional designs. Learn which style suits your home and lifestyle.",
      category: "Interior Design",
      date: "Dec 28, 2024",
      author: "Sneha Gupta",
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=85&w=800",
      readTime: "6 min read"
    },
    {
      slug: 'rera-guide',
      title: "Understanding RERA: A Homebuyer's Essential Guide",
      excerpt: "Everything you need to know about RERA regulations and how they protect your property investment.",
      category: "Real Estate",
      date: "Dec 25, 2024",
      author: "Rajesh Kumar",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=85&w=800",
      readTime: "8 min read"
    },
    {
      slug: 'color-psychology',
      title: "Color Psychology in Interior Design",
      excerpt: "How colors affect mood and productivity. Choose the right palette for each room in your home.",
      category: "Interior Design",
      date: "Dec 22, 2024",
      author: "Priya Sharma",
      image: "https://images.unsplash.com/photo-1615529182904-14819c35db37?q=85&w=800",
      readTime: "5 min read"
    }
  ];

  const articles = allArticles.slice(0, visibleArticles);
  const hasMore = visibleArticles < allArticles.length;

  const handleLoadMore = () => {
    setLoading(true);
    // Simulate loading delay
    setTimeout(() => {
      setVisibleArticles(prev => Math.min(prev + 3, allArticles.length));
      setLoading(false);
      toast.success('More articles loaded!');
    }, 500);
  };

  return (
    <div className="min-h-screen bg-white py-32" data-testid="blogs-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4" data-testid="blogs-title">Insights & Articles</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Expert insights, tips, and trends in real estate, interior design, and property management.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {articles.map((article, idx) => (
            <Link 
              key={idx} 
              to={`/blogs/${article.slug}`}
              className="bg-white border border-gray-200 hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden" 
              data-testid={`blog-card-${idx}`}
            >
              <div className="h-56 overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs px-3 py-1 bg-[#F5F5F0] text-[#15803D] font-medium rounded-full">{article.category}</span>
                  <span className="text-xs text-gray-500">{article.readTime}</span>
                </div>
                <h2 className="text-xl font-bold mb-3 hover:text-[#15803D] transition-colors">{article.title}</h2>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{article.excerpt}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {article.date}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {hasMore && (
          <div className="mt-16 text-center">
            <Button 
              variant="outline"
              size="lg"
              className="rounded-none"
              onClick={handleLoadMore}
              disabled={loading}
              data-testid="load-more-btn"
            >
              {loading ? (
                <>Loading...</>
              ) : (
                <>Load More Articles <ArrowRight className="ml-2 w-4 h-4" /></>
              )}
            </Button>
          </div>
        )}
        
        {!hasMore && (
          <div className="mt-16 text-center">
            <p className="text-gray-500">You&apos;ve reached the end of our articles.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;